import * as sonbp from "sonbp";
import * as ifbase from "../base";
import { poscaClipImageEditor } from "../listener/editor";
import { itemMani } from ".";

// -----------------------------------------------
// 画像枠独自の操作
// -----------------------------------------------

/**
 * 画像枠に画像を短辺フィットさせる。
 * @summary force=trueなら無条件にフィット。falseなら画像枠より小さい場合のみフィット。
 */
export function photoFit(box: sonbp.object.groupItem, img: sonbp.object.baseItem, deg: number, force: boolean, func: (item: sonbp.object.baseItem) => void): void {

	//枠
	let boxDeg = -Number(box.info.vendor_property.ffpsc?.box_deg ?? 0);
	let boxRad = sonbp.util.c2d.rad(boxDeg);
	let boxCmd = sonbp.util.c2d.getRotateCommands(box.clip_item._commands, -boxRad, box.clip_item.points[4]);
	let boxRect = sonbp.util.c2d.getRectOfCommands(boxCmd);
	//1px外側を閾値とする
	boxRect = new sonbp.type.rectPoint(boxRect.x1 - 1, boxRect.y1 - 1, boxRect.x2 + 1, boxRect.y2 + 1);

	//画像
	let imgDeg = img.deg;
	let imgPoints = img.points;
	let imgRad = sonbp.util.c2d.rad(imgDeg);
	imgPoints = sonbp.util.c2d.getRotatePoints(imgPoints, -imgRad, imgPoints[4]);

	//角度
	let fitDeg = Math.round((imgDeg - boxDeg + deg) / 90) * 90;

	if (fitDeg % 180 !== 0) {
		imgPoints = sonbp.util.c2d.getRotatePoints(imgPoints, sonbp.util.c2d.rad(-90), imgPoints[4]);
	}
	let imgRect = sonbp.util.c2d.getRectOfPoints(imgPoints);
		
	fitDeg = sonbp.util.c2d.normDeg(fitDeg + boxDeg - imgDeg);

	let pt = sonbp.util.c2d.getAdjustPoint(new sonbp.type.point(imgRect.width, imgRect.height), new sonbp.type.point(boxRect.width, boxRect.height), sonbp.constant.fit4Clip.SHORT);

	let sx = pt.x / imgRect.width;
	let sy = pt.y / imgRect.height;

	img.rotate(fitDeg);
	if (force || Math.max(sx, sy) > 1.0) {
		img.scale(sx, sy);
	}

	var arect = img.rect;

	img.move(boxRect.getCenterPoint().x - arect.getCenterPoint().x, boxRect.getCenterPoint().y - arect.getCenterPoint().y);

	//ベンダープロパティ設定
	func(img);

	if (img.info.vendor_property.ffpsc?.file_id != "@DUMMY@") {
		adjustPositionWaku(box, img);
	}
}

/**
 * 画像の回転（枠を基準に90度回転）
 */
export function chkRotate90(box: sonbp.object.groupItem, imgDeg: number, deg: number): number {
	//枠
	const boxDeg = -Number(box.info.vendor_property.ffpsc?.box_deg ?? 0);
	const boxRad = sonbp.util.c2d.rad(boxDeg);

	//画像
	const imgRad = sonbp.util.c2d.rad(imgDeg);

	//角度
	deg = Math.round((imgDeg - boxDeg + deg) / 90) * 90;

	return sonbp.util.c2d.normDeg(deg + boxDeg - imgDeg);
}

/**
 * 画像の位置・拡縮率調整
 * @summary 以下の場合、サイズ・位置を調整する。
 * 　　　　　　・拡縮率の上限下限超過
 * 　　　　　　・拡縮率調整・移動後の位置で画像枠に余白あり。
 */
export function adjustPositionWaku(box: sonbp.object.baseItem, item: sonbp.object.baseItem): void {

	//画像枠
	let boxWakuPita = box.info.vendor_property.ffpsc?.waku_pita ?? false;
	let imgRad = sonbp.util.c2d.rad(item.deg);
	let boxCmd = sonbp.util.c2d.getRotateCommands(box.clip_item._commands, -imgRad, box.clip_item.points[4]);
	let boxRect = sonbp.util.c2d.getRectOfCommands(boxCmd);
	//1px外側を閾値とする
	boxRect = new sonbp.type.rectPoint(boxRect.x1 - 1, boxRect.y1 - 1, boxRect.x2 + 1, boxRect.y2 + 1);

	//拡縮率調整
	let sc = commonScaleCheck(box, item.points, item.info.vendor_property);

	if (sc !== 1) {
		let pts = item.points;
		item.scale(sc, sc);
		let pts2 = item.points;
		item.move(pts[4].x - pts2[4].x, pts[4].y - pts2[4].y);
	}

	//位置調整
	if (boxWakuPita) {

		let mx = 0;
		let my = 0;

		let preImgPoints = item.points;
		let imgPoints = sonbp.util.c2d.getRotatePoints(preImgPoints, -imgRad, box.clip_item.points[4]);
		let imgRect = sonbp.util.c2d.getRectOfPoints(imgPoints);

		if (boxRect.x2 > imgRect.x2) {
			mx = boxRect.x2 - imgRect.x2;
		}
		if (boxRect.x1 < imgRect.x1) {
			mx = boxRect.x1 - imgRect.x1;
		}
		if (boxRect.y2 > imgRect.y2) {
			my = boxRect.y2 - imgRect.y2;
		}
		if (boxRect.y1 < imgRect.y1) {
			my = boxRect.y1 - imgRect.y1;
		}

		if (mx !== 0 || my !== 0) {

			let cpt = imgPoints[4];
			let bf = new sonbp.type.point(cpt.x + mx, cpt.y + my);
			let af = sonbp.util.c2d.getPointWithRotate(box.clip_item.points[4], bf, imgRad);
			mx = af.x - preImgPoints[4].x;
			my = af.y - preImgPoints[4].y;
			console.debug("adjust waku move2(waku_pita:true).", "x", mx, "y", my);
			item.move(mx, my);
		}
	}
}

/**
 * 拡縮制限チェック
 * @summary 画像枠より内側になってはいけない。且つ規定倍率の範囲内でなくてはいけない。
 */
export function chkScaleOverWaku(ifdto: ifbase.ifDto, ed: poscaClipImageEditor, aMatrix: number[], aPoints: sonbp.type.point[], s: { sx: number, sy: number }): void {

	let vi = (<any>ed)._editData.v as sonbp.listener.editor.vitem;

	let box = (<any>ed)._select.items[0] as sonbp.object.groupItem;
	let item = vi.item as sonbp.object.xobjectItem;

	//閾値
	let boxRect = box.rect;

	//n1 今の座標
	let pts = sonbp.util.c2d.setPointsMatrix(aPoints, aMatrix);
	let n1rect = sonbp.util.c2d.getRectOfPoints(pts);

	//console.debug("scale1", "n1rect", n1rect);

	//n2 拡大後
	//scale
	let n2matrix = sonbp.util.c2d.matrixScale(aMatrix, s.sx, s.sy);
	let n2points = sonbp.util.c2d.setPointsMatrix(aPoints, n2matrix);
	//let n2rect = sonbp.util.c2d.getRectOfPoints(n2points);

	//各縮率チェック
	if (s.sx < 0 || s.sy < 0) {
		s.sx = s.sy = Math.max(boxRect.width / n1rect.width, boxRect.height / n1rect.height);
	} else {
		let sc = commonScaleCheck(box, n2points, item.info.vendor_property);
		s.sx = s.sy = sc * s.sx;
	}
}

/**
 * 拡縮率チェック本体
 * @summary 画像サイズの拡大は、以下の大きい方を上限とする。
 * 　　　　　　・元サイズの1.6倍
 * 　　　　　　・画像枠に余白ができないサイズ
 * @summary 画像サイズの縮小は、画像枠に余白ができないサイズを下限とする。
 */
function commonScaleCheck(boxItem: sonbp.object.baseItem, itemPoints: sonbp.type.point[], itemVp: any): number {

	/*
	 * メモ：
	 * 画像枠の角度はマトリクス使わず直接drawcommandの座標で傾きを表現している。
	 * そのため画像側の回転角度を戻した状態での矩形比較を基準としてチェックする。
	 */

	let imgRad = sonbp.util.c2d.getRadian(itemPoints[4], itemPoints[5]);

	//画像枠
	let boxWakuPita = boxItem.info.vendor_property.ffpsc?.waku_pita ?? false;
	let boxBaseCmd = sonbp.util.c2d.getRotateCommands(boxItem.clip_item._commands, -imgRad, boxItem.clip_item.points[4]);
	let boxBaseRect = sonbp.util.c2d.getRectOfCommands(boxBaseCmd);
	//1px外側を閾値とする
	boxBaseRect = new sonbp.type.rectPoint(boxBaseRect.x1 - 1, boxBaseRect.y1 - 1, boxBaseRect.x2 + 1, boxBaseRect.y2 + 1);

	//等倍矩形
	let or = itemVp?.ffpsc?.org_rect;
	if (or === undefined) {
		throw new Error("org_rect undefined. vp=" + itemVp?.ffpsc);
	}
	let orgPureRect = new sonbp.type.rectPoint(or[0], or[1], or[2], or[3]);

	//1.6倍矩形
	let max = ifbase.constants.PHOTOP_SCALE_MAX;
	let maxRect = sonbp.util.c2d.getRect(orgPureRect, [max, 0, 0, max, 0, 0]);

	//1.6倍矩形より画像枠矩形のほうが大きいなら、画像枠矩形を上限とする
	if (maxRect.width < boxBaseRect.width || maxRect.height < boxBaseRect.height) {
		max = Math.max(boxBaseRect.width / orgPureRect.width, boxBaseRect.height / orgPureRect.height);
		maxRect = sonbp.util.c2d.getRect(orgPureRect, [max, 0, 0, max, 0, 0]);
		console.debug("waku > max.", max);
	} else {
		//console.debug("max.", max);
	}

	//枠ぴったりＯＮ：1倍矩形、なし：0.25倍矩形
	let min = boxWakuPita ? 1 : ifbase.constants.PHOTOP_SCALE_MIN;
	let minRect = sonbp.util.c2d.getRect(boxBaseRect, [min, 0, 0, min, 0, 0]);

	//画像
	let imgBasePoints = sonbp.util.c2d.getRotatePoints(itemPoints, -imgRad, itemPoints[4]);
	let imgBaseRect = sonbp.util.c2d.getRectOfPoints(imgBasePoints);

	//サイズ調整
	let sx = 1;
	let sy = 1;

	//拡縮率
	//max
	if (imgBaseRect.width > maxRect.width) {
		sx = maxRect.width / imgBaseRect.width;
		console.debug("> sx", sx);
	}
	if (imgBaseRect.height > maxRect.height) {
		sy = maxRect.height / imgBaseRect.height;
		console.debug("> sy", sy);
	}
	//min
	if (imgBaseRect.width < minRect.width) {
		sx = minRect.width / imgBaseRect.width;
		console.debug("< sx", sx);
	}
	if (imgBaseRect.height < minRect.height) {
		sy = minRect.height / imgBaseRect.height;
		console.debug("< sy", sy);
	}

	let sc = boxWakuPita ? Math.max(sx, sy) : Math.min(sx, sy);

	console.debug("scale check.", "s", sc, "sm", imgBaseRect.width * sc / orgPureRect.width);

	return sc;
}

/**
 * 画像の位置チェック（枠ぴったりＯＦＦ用）
 * @summary 画像が枠から超えすぎているか
 */
export function chkIfImageExceedWaku(box: sonbp.object.baseItem, vitem: sonbp.listener.editor.vitem): boolean {

	const s = ifbase.constants.PHOTOP_INSIDE_MIN;
	const sc = new sonbp.type.point(s, s);
	const box_ = box.clone();
	let boxCmds_ = box_.clip_item._commands;

	const pts1 = sonbp.util.c2d.getAnyPtOfCommands(boxCmds_);
	boxCmds_ = sonbp.util.c2d.setScaleCommands(boxCmds_, sc);
	const pts2 = sonbp.util.c2d.getAnyPtOfCommands(boxCmds_);
	const pt = new sonbp.type.point(pts1.x - pts2.x, pts1.y - pts2.y);
	boxCmds_ = sonbp.util.c2d.setMoveCommands(boxCmds_, pt);

	const imagePts = vitem.getPoints();
	const imageCmds = sonbp.util.c2d.getCommandsOfAnyPoints([imagePts[0], imagePts[6], imagePts[8], imagePts[2]]);

	if (!sonbp.util.c2d.chkInnerCommand(imageCmds, boxCmds_) && !sonbp.util.c2d.chkInnerCompletely(boxCmds_, imageCmds)) {
		// 画像が枠から超えすぎている
		return true;
	}

	return false;
}

/**
 * 画像枠に画像を配置する
 */
export function setPhotoImg(ifdto: ifbase.ifDto, boxId: string, file_key: string, file_id: string, img_size: { w: number, h: number }, keep_flg, callback: (resKey: string) => void): void {

	let resKey = "";

	ifbase.util.getResouseKeyAsync(ifdto, file_key).then((d: ifbase.ImageResourceModel) => {

		// 実際の画像サイズは引数に従う
		if (img_size != null) {
			d.orgWidth = img_size.w;
			d.orgHeight = img_size.h;
		} else {
			//フロント実装までの暫定
			d.orgWidth = d.Width;
			d.orgHeight = d.Height;
		}

		let v = ifdto.bpView;
		let refInfo = sonbp.tool.item.getRefInfo(v, boxId);

		//画像の配置
		return v._om.itemChangeAsync([refInfo], (itemRefs) => {

			return new sonbp.type.promise((ss, ff) => {

				try {
					let ratio = 1 / sonbp.util.c2d.getUnit4Px(sonbp.constant.unit.PT, v.initialize.desiredDpi);

					let clip = itemRefs[0].item as sonbp.object.groupItem;
					let photo = clip.childs[0] as sonbp.object.xobjectItem;

					let preSubType = photo.subtype;

					photo.src = d.ResourceKey;
					photo.subtype = d.Subtype;
					photo.size = [
						sonbp.util.bp.i2u(d.Width, v._exp.docUnit, v._exp.userUnitValue),
						sonbp.util.bp.i2u(d.Height, v._exp.docUnit, v._exp.userUnitValue)
					];

					//補正画像の差替えなら位置角度拡大はキープ
					if (keep_flg) {

						//再編集だとyaclのsubtype変わるので補正
						if (photo.subtype == sonbp.constant.itemSubType.FORM &&
							preSubType == sonbp.constant.itemSubType.IMAGE) {

							photo.matrix = [
								photo.matrix[0] / photo.size[0],
								photo.matrix[1] / photo.size[0],
								photo.matrix[2] / photo.size[1],
								photo.matrix[3] / photo.size[1],
								photo.matrix[4],
								photo.matrix[5]
							];
						}

						let vp = photo.info.vendor_property.ffpsc;
						vp.file_id = file_id;
						vp.org_rect = [0, 0, d.orgWidth * ratio, d.orgHeight * ratio];

						adjustPositionWaku(clip, photo);

						resKey = d.ResourceKey;
						ss();
						return;
					}

					photo.deg = 0;
					if (photo.subtype == sonbp.constant.itemSubType.IMAGE) {
						photo.matrix = [photo.size[0] * ratio, 0, 0, photo.size[1] * ratio, 0, 0]
					} else {
						photo.matrix = [1, 0, 0, 1, 0, 0];
					}

					// 短辺フィット
					photoFit(clip, photo, 0, true, function (item) {

						// アスペクト比変更可否の設定
						if (v.initialize.isAspectNg) item.info.modeOn(sonbp.constant.editModeType.ASPECT_NG);

						// ベンダープロパティ設定
						let vp = item.info.vendor_property.ffpsc;
						vp.file_id = file_id;
						vp.org_rect = [0, 0, d.orgWidth * ratio, d.orgHeight * ratio];
						// 枠ぴったりＯＦＦ(デフォルト)
						clip.info.vendor_property.ffpsc.waku_pita = false;

						console.debug("org_size debug", "svr-", d.Width, d.Height, "org-", d.orgWidth, d.orgHeight);
					});

					resKey = d.ResourceKey;

					ss();
				}
				catch (e) {

					ff(e);
				}
			});
		});

	}).then(() =>{

		callback(resKey);
	}).catch(e => {

		ifdto.callbackError(e);
	});
}

/**
 * 画像枠から画像を削除する
 * @summary 画像削除＝ダミー画像への差替え
 */
export function removePhotoImg(ifdto: ifbase.ifDto, id: string, callback: (resKey: string) => void): void {

	let v = ifdto.bpView;
	setPhotoImg(ifdto, id, "RES@DUMMY", "@DUMMY@", { w: 1, h: 1 }, false, callback);
}

/**
 * 画像枠Ａ・Ｂの画像を入れ替える
 */
export function exchangePhotoImg(ifdto: ifbase.ifDto, boxId_A: string, boxId_B: string, succesFunc: () => void, errorFunc: (e) => void): void {

	let v = ifdto.bpView;

	interface boxInfo {
		box: sonbp.object.groupItem,
		boxItemtype: string,
		boxIdx: string,
		imgSrc: string,
		imgSubtype: string,
		imgSize: number[],
		imgVp: any,
		imgMatrix: number[],
		imgDeg: number
	};

	let refInfos: sonbp.type.itemRefInfo[];
	let boxA: boxInfo;
	let boxB: boxInfo;

	try {
		refInfos = sonbp.tool.item.getRefInfos(v, [boxId_A, boxId_B]);

		//入替え前の情報退避
		for (let i = 0; i < refInfos.length; i++) {
			let item = refInfos[i].item as sonbp.object.groupItem;
			let img = item.childs[0] as sonbp.object.xobjectItem;
			let data = {
				box: item,
				boxItemtype: item.info.vendor_property.ffpsc.item_type,
				boxIdx: item.info.vendor_property.ffpsc.idx,
				imgSrc: img.src,
				imgSubtype: img.subtype,
				imgSize: img.size.concat(),
				imgVp: JSON.parse(JSON.stringify(img.info.vendor_property.ffpsc)),
				imgMatrix: img.matrix.concat(),
				imgDeg: img.deg
			};

			if (refInfos[i].item.id == boxId_A)
				boxA = data;
			else
				boxB = data;
		}
	}
	catch (e) {

		errorFunc(e);
		return;
	}

	console.debug("[sonbpIf-exchangePhotoImg] A=" + boxA.imgVp.file_id + ", B=" + boxB.imgVp.file_id);

	new sonbp.type.promise((resolve, reject) => {

		//未配置枠あればダミー画像準備
		if (boxA.imgVp.file_id == "@DUMMY@" || boxB.imgVp.file_id == "@DUMMY@") {

			ifbase.util.getResouseKey(ifdto, "RES@DUMMY").then((d) => {
				resolve(d);
			}).catch((e) => {
				reject(e);
			});
		} else {
			resolve()
		}

	}).then((dummy: ifbase.ImageResourceModel) => {

		//画像の入替え
		return v._om.itemChangeAsync(refInfos, (itemRefs) => {

			return new sonbp.type.promise((ss, ff) => {

				try {

					for (let i = 0; i < itemRefs.length; i++) {

						let fromBox = (itemRefs[i].item.id == boxId_A) ? boxB : boxA;

						let ratio = 1 / sonbp.util.c2d.getUnit4Px(sonbp.constant.unit.PT, v.initialize.desiredDpi);

						let clip = itemRefs[i].item as sonbp.object.groupItem;
						let photo = clip.childs[0] as sonbp.object.xobjectItem;
						let restoreDeg = 0;

						if (fromBox.imgVp.file_id == "@DUMMY@") {
							photo.src = dummy.ResourceKey;
							photo.subtype = dummy.Subtype;
							photo.size = [
								sonbp.util.bp.i2u(dummy.Width, v._exp.docUnit, v._exp.userUnitValue),
								sonbp.util.bp.i2u(dummy.Height, v._exp.docUnit, v._exp.userUnitValue)
							];
							if (photo.subtype == sonbp.constant.itemSubType.IMAGE) {
								photo.matrix = [photo.size[0] * ratio, 0, 0, photo.size[1] * ratio, 0, 0]
							} else {
								photo.matrix = [1, 0, 0, 1, 0, 0];
							}
							photo.deg = 0;
						} else {
							photo.src = fromBox.imgSrc;
							photo.subtype = fromBox.imgSubtype;
							photo.size = fromBox.imgSize.concat();
							photo.matrix = fromBox.imgMatrix.concat();
							photo.deg = fromBox.imgDeg;
							restoreDeg = -fromBox.imgDeg
						}

						// 短辺フィット
						photoFit(clip, photo, restoreDeg, true, function (item) {

							// アスペクト比変更可否の設定
							if (v.initialize.isAspectNg) item.info.modeOn(sonbp.constant.editModeType.ASPECT_NG);

							// ベンダープロパティ設定
							let vp = item.info.vendor_property.ffpsc;
							vp.file_id = fromBox.imgVp.file_id;
							vp.org_rect = fromBox.imgVp.org_rect?.concat();
							// 枠ぴったりＯＦＦ(デフォルト)
							clip.info.vendor_property.ffpsc.waku_pita = false;
						});
					}

					ss();
				}
				catch (e) {

					ff(e);
				}
			});
		});

	}).then(() => {

		itemMani.selectItem(ifdto, boxB.boxItemtype, boxB.boxIdx);
		succesFunc();
	}).catch(e => {

		errorFunc(e);
	});
}

/**
* 画像枠に枠ぴったりプロパティをセット
*/
export function setPhotoWakuPita(ifdto: ifbase.ifDto, boxId: string, doPita: boolean, callback: ()=>void): void {

	const v = ifdto.bpView;
	const refInfo = sonbp.tool.item.getRefInfo(v, boxId);

	v._om.itemChangeAsync([refInfo], (itemRefs) => {

		return new sonbp.type.promise((ss, ff) => {

			try {
				const clip = itemRefs[0].item as sonbp.object.groupItem;

				clip.info.vendor_property.ffpsc.waku_pita = doPita;
				if (doPita) {
					// 枠ぴったりＯＮにした場合、位置・拡縮率調整
					adjustPositionWaku(clip, clip.childs[0]);
				}

				ss();
			}
			catch (e) {

				ff(e);
			}
		});
	}).then(() => {

		callback();
	}).catch(e => {

		ifdto.callbackError(e);
	});
}